import { StreamGroup } from '@/renderer/scripts/utils/StreamManager/lib/StreamGroup';
import { SystemSetting } from '@/renderer/scripts/utils/StreamManager/utils/SystemSetting';
import { RangeLimit } from '@/renderer/scripts/utils/storage';
import { IS_BROWSER } from '@/common/constant';
import { LoaderConfig } from '@/renderer/scripts/utils/CommonLoader';

let fs: typeof import('fs');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
}
// 利用闭包做一个缓存，在每次读取系统配置后，都会更新当前变量
const cache: { uploadSetting: null | RangeLimit; downloadSetting: null | RangeLimit } = {
  uploadSetting: null,
  downloadSetting: null,
};

// 默认的带宽限制 128(kb/s)
const defaultOffsetLimit = 128 * 1024;

/**
 * Loader对应的组管理器
 * @description 目的是为了统一管理所有的loader组的速度
 */
export class StreamManager {
  // 流管理器分（上传/下载）两种管理器
  private _type: 'upload' | 'download';
  // 所有streamGroup的集合
  private _streamGroups: Map<string, StreamGroup>;
  // 弱限速
  private _weakLimit = false;
  // 弱限速阈值
  private _weakLimitBytes = 0;
  // 预留带宽
  private _offsetLimit = defaultOffsetLimit;

  constructor(type: 'upload' | 'download') {
    this._type = type;
    this._streamGroups = new Map();
  }

  // 开始测速
  public startDetectSpeed() {
    logger.ui(`[StreamManager] 开始测速`);
    this.setWeakLimit(0, 0);
  }
  // 停止测速
  public stopDetectSpeed(maxSpeed: number) {
    logger.ui(`[StreamManager] 开启弱限速${maxSpeed}, 设置偏移量 ${defaultOffsetLimit}`);
    this.setWeakLimit(defaultOffsetLimit, maxSpeed);
  }

  /**
   * 设置弱限速
   * @param offsetBytes
   * @param weakLimitBytes
   */
  public setWeakLimit(offsetBytes: number, weakLimitBytes: number) {
    this._offsetLimit = offsetBytes;
    if (offsetBytes === undefined || offsetBytes <= 0) {
      this._setWeakLimit(false);
    } else {
      this._setWeakLimit(true, { weakLimitBytes, offsetLimit: this._offsetLimit });
    }
    this._fixSpeed();
  }
  private _setWeakLimit(weakLimit: boolean, options?: { weakLimitBytes: number; offsetLimit: number }) {
    this._weakLimit = weakLimit;
    if (weakLimit && options) {
      this._weakLimitBytes = options.weakLimitBytes - options.offsetLimit;
    }
  }
  /**
   * 创建一个group
   * @description
   * 每一个groupId都对应了一个Loader
   * 每一个Uploader内部都有很多个stream
   * @param loaderId
   * @param fileName 文件名
   */
  public createGroup(loaderId: string, fileName: string): StreamGroup {
    // 创建一个内部的stream群组
    const streamGroupOrigin = new StreamGroup(this._type, fileName);
    // manager内部放置一个引用
    this._streamGroups.set(loaderId, streamGroupOrigin);
    // 调整限速
    this._fixSpeed();
    // 返回创建的实例
    return this._setProxy(streamGroupOrigin);
  }

  /**
   * 从合集中删除一个group
   * @description 在任务[完成]/[出错]的时候，需要删掉整个group
   * @param loaderId
   */
  public deleteGroupByLoaderId(loaderId: string): boolean {
    const currentGroup = this._streamGroups.get(loaderId);
    currentGroup?.destroyAllStreams();
    return this._streamGroups.delete(loaderId);
  }

  /**
   * 尝试去触发一次速度的自更新
   * @description streamManager实例会接收到信息后，主动读取系统配置（无缓存），
   * 随后根据正在运行的stream实例来更新速度
   */
  public triggerFixSpeed(): void {
    // setTimeout的目的是为了脱离当前执行栈，不阻塞赋值
    setTimeout(() => {
      // this: StreamManager
      this._fixSpeed();
    }, 0);
  }

  logger() {
    /* this._streamGroups.forEach((streamGroup: StreamGroup) => {
      logger.ui(
        `streamGroup-${streamGroup.fileName}的stream数量: ${streamGroup.streamCount}个,限速：${streamGroup._limit},每一个限速${streamGroup._limitKbs}kb/s`,
      );
    }); */
  }
  /* logger() {
    console.log(`=========${this._type}StreamManager======`);
    console.log(`当前streamGroups的数据量${this._streamGroups.size}`);
    console.log('-for-');
    this._streamGroups.forEach((streamGroup: StreamGroup) => {
      console.log(
        `streamGroup-${streamGroup.fileName}的stream数量: ${streamGroup.streamCount}个,限速：${streamGroup._limit},每一个限速${streamGroup._limitKbs}kb/s`,
      );
    });
    console.log(`=========${this._type}StreamManager======`);
  } */
  /**
   * 调整所有的stream中的速度限制
   * @description
   * 场景： 目前只有在stream数量发生改变的时候，才会进行调整
   * TODO loganylwu 1. 在stream发生变化触发后(周期内)，若读取系统配置和上次并无变化，且stream也在结束后，迅速的补上了对应的数量，是否可以不再进行速度调整？[现在是先根据stream调小，再调回来，意义不大，且消耗性能]
   * TODO loganylwu 2. 在stream发生变化触发后(周期内)，若系统配置一直都是自动（speedLimit = false)，是否可以直接跳过遍历？
   */
  _fixSpeed() {
    // 获取系统配置
    const rangeLimit = SystemSetting.getSystemTransferSetting(this._type);
    const { speedLimit, speedLimitSize, speedLimitUnit, batchAuto, batchLimit } = rangeLimit;
    const fileLimitKbs = SystemSetting.convertTransferSpeed(speedLimitSize, speedLimitUnit);
    // 内部限速
    if (this._weakLimit) {
      // 整体限速值（单位kb/s)
      const totalSpeedLimitSizeKbs = (() => {
        let systemTotalLimitKbs = -1;
        if (this._weakLimit) {
          systemTotalLimitKbs = this._weakLimitBytes;
        } else {
          const systemFileLimit = batchAuto ? LoaderConfig.ChunkParallelLimit : batchLimit;
          systemTotalLimitKbs = Math.round(fileLimitKbs * systemFileLimit);
        }
        return systemTotalLimitKbs;
      })();
      // 整体存活的stream数量
      const totalStreamCount = (() => {
        let count = 0;
        // 计算所有群组的所有stream数量
        this._streamGroups.forEach((streamGroup: StreamGroup) => {
          count += streamGroup.streamCount;
        });
        return count;
      })();
      // 遍历所有的streamGroup,调整速度
      // console.warn(`limit ${needLimit} | ${totalStreamCount} | ${totalSpeedLimitSizeKbs}`);
      // 每一个 streamGroup 对应一个文件
      this._streamGroups.forEach((streamGroup: StreamGroup) => {
        streamGroup.fixSpeedByLimit(true, {
          fileSpeedLimitSizeKbs: null,
          totalStreamCount,
          totalSpeedLimitSizeKbs,
        });
      });
    }
    // 限速
    else if (speedLimit) {
      this._streamGroups.forEach((streamGroup: StreamGroup) => {
        streamGroup.fixSpeedByLimit(true, {
          fileSpeedLimitSizeKbs: fileLimitKbs,
          totalStreamCount: null,
          totalSpeedLimitSizeKbs: null,
        });
      });
    }
    // 不限速
    else {
      this._streamGroups.forEach((streamGroup: StreamGroup) => {
        streamGroup.fixSpeedByLimit(false);
      });
    }
  }
  /**
   * 增加监听
   * 在【streamCount】发生改变的时候，重新调整限速配额（fixSpeed）。
   * 在【totalSentBytes】发生改变的时候，重新统计传输数据
   *
   * @param streamGroupOrigin
   * @private
   */
  private _setProxy(streamGroupOrigin: StreamGroup) {
    // 注意：声明函数的位置要在StreamManager属性里面，这样this才是StreamManager实例
    const setHandler = (target: StreamGroup, prop: PropertyKey, value: any, receiver: any): boolean => {
      // @ts-ignore
      target[prop] = value;
      if (prop === 'streamCount') {
        this.triggerFixSpeed();
        this.logger();
      }
      // if (prop === 'totalSentBytes') {
      // }
      return true;
    };
    return new Proxy(streamGroupOrigin, {
      set: setHandler,
    });
  }
}

const uploadStreamManager = new StreamManager('upload');
const downloadStreamManager = new StreamManager('download');
// @ts-ignore
window.uploadStreamManager = uploadStreamManager;
// @ts-ignore
window.downloadStreamManager = downloadStreamManager;
export { uploadStreamManager, downloadStreamManager };
